import {
  Image,
  Menu,
  Portal,
  Separator,
  useBreakpointValue,
  useToken,
} from '@chakra-ui/react';
import { Menu as MenuIcon } from 'lucide-react';
import { HStack, Link, Pressable } from 'components/ui';
import { useAffiliate } from '../../api/affiliates/useAffiliate';
import { useNavigateWithParams } from '../../hooks/useNavigateWithParams';
import { getLoginNavLinks } from './navLinks';

const aybleLogo = '/FA__ayble_health_Logo_Isabelline.png';

export const LoginHeader = () => {
  const { affiliate } = useAffiliate();
  const navigate = useNavigateWithParams();
  const isMobile = useBreakpointValue({ base: true, sm: false });
  const [secondaryColor] = useToken('colors', ['secondary.500']);

  const { howItWorksUrl, faqUrl } = getLoginNavLinks(affiliate);
  const navItems = [
    { label: 'How it works', url: howItWorksUrl },
    // The marketing site has no FAQ page, so only surface FAQs when the
    // affiliate supplies its own FAQ link.
    ...(faqUrl ? [{ label: 'FAQs', url: faqUrl }] : []),
  ];

  return (
    <HStack
      alignItems="center"
      justifyContent="space-between"
      px={{ base: 5, md: 8 }}
      py={4}
      w="100%"
    >
      <HStack alignItems="center" gap={affiliate?.logo ? 3 : 0}>
        <Pressable
          onClick={() => navigate('/')}
          role="link"
          aria-label="Ayble Health home"
          style={{ cursor: 'pointer' }}
        >
          <Image
            src={aybleLogo}
            alt="Ayble Health logo"
            h="56px"
            w="90px"
            objectFit="contain"
          />
        </Pressable>

        {!!affiliate?.logo && (
          <>
            <Separator
              orientation="vertical"
              width="1px"
              h="44px"
              bgColor="secondary.200"
            />
            <img
              src={affiliate.logo}
              alt={`${affiliate.utm_source} logo`}
              data-testid="login-cobranding-logo"
              style={{
                height: '56px',
                width: 'auto',
                maxWidth: '160px',
                objectFit: 'contain',
              }}
            />
          </>
        )}
      </HStack>

      {isMobile ? (
        <Menu.Root>
          <Menu.Trigger asChild>
            <Pressable
              aria-label="Open navigation menu"
              data-testid="login-nav-menu"
            >
              <MenuIcon size={24} color={secondaryColor} />
            </Pressable>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content>
                {navItems.map(({ label, url }) => (
                  <Menu.Item
                    key={label}
                    value={label}
                    onClick={() =>
                      window.open(url, '_blank', 'noopener,noreferrer')
                    }
                  >
                    {label}
                  </Menu.Item>
                ))}
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
      ) : (
        <HStack gap={8} alignItems="center">
          {navItems.map(({ label, url }) => (
            <Link
              key={label}
              href={url}
              isExternal
              _text={{
                color: 'secondary.500',
                fontWeight: 'medium',
                textTransform: 'none',
                fontSize: 'sm',
              }}
            >
              {label}
            </Link>
          ))}
        </HStack>
      )}
    </HStack>
  );
};
